import type { Vessel } from '@/store/vessels/vessel.dto';
import { getCountries } from '@/utils/getCountries';
import { convertLength, type LengthUnit } from '@/utils/unitConversion';
import {
  CRUISING_LOCATION_OPTIONS,
  HULL_MATERIAL_OPTIONS,
  type QuizAnswers,
  USAGE_TYPE_OPTIONS,
  VESSEL_TYPE_OPTIONS,
} from '@pages/MyYachts/components/modals/QuizModal/quizModal.data';

/**
 * What core-ai's `POST /api/ai/vessel/extract` returns under `vessel`. Everything is a
 * string (or null); dimensions carry the unit printed on the document and are converted
 * here, never guessed from the user's display preference.
 */
type ExtractedVessel = {
  vessel_name?: string | null;
  make?: string | null;
  model?: string | null;
  year_built?: number | string | null;
  length_overall?: number | string | null;
  length_unit?: string | null;
  registration_number?: string | null;
  description?: string | null;
  /** Flag state as printed on the document. */
  country?: string | null;
  home_port?: string | null;
  vessel_type?: string | null;
  country_built?: string | null;
  beam?: number | string | null;
  beam_unit?: string | null;
  draft?: number | string | null;
  draft_unit?: string | null;
  engines?: string | null;
  hull_material?: string | null;
  guest_cabins?: number | string | null;
  crew_count?: number | string | null;
  cruising_areas?: string | null;
  usage_type?: string | null;
  estimated_value?: number | string | null;
};

type AutofillAnswerKey =
  | 'flag_state'
  | 'vessel_type'
  | 'country_built'
  | 'beam_m'
  | 'draft_m'
  | 'engines'
  | 'hull_material'
  | 'guest_cabins'
  | 'crew_count'
  | 'home_port'
  | 'cruising_areas'
  | 'usage_type'
  | 'estimated_value';

export type VesselAutofill = {
  vessel: Partial<
    Pick<
      Vessel,
      | 'vessel_name'
      | 'make'
      | 'model'
      | 'year_built'
      | 'length_overall'
      | 'registration_number'
      | 'description'
    >
  >;
  answers: Partial<Pick<QuizAnswers, AutofillAnswerKey>>;
};

const COUNTRY_ALIASES: Record<string, string> = {
  usa: 'US',
  'united states of america': 'US',
  uk: 'GB',
  'united kingdom': 'GB',
  'great britain': 'GB',
  england: 'GB',
};

const findCountryCode = (name: string) => {
  const countries = getCountries();
  const lower = name.toLowerCase().trim();
  const alias = COUNTRY_ALIASES[lower];
  const found = alias
    ? countries.find((country) => country.code === alias)
    : countries.find((country) => country.name.toLowerCase() === lower) ||
      countries.find((country) => country.code.toLowerCase() === lower) ||
      countries.find((country) => country.name.toLowerCase().includes(lower)) ||
      countries.find((country) => lower.includes(country.name.toLowerCase()));
  return found?.code;
};

const toNumber = (value?: number | string | null) => {
  if (value === undefined || value === null) return undefined;
  const match = String(value).match(/[\d.]+/);
  const parsed = match ? Number(match[0]) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const toInteger = (value?: number | string | null) => {
  const parsed = toNumber(value);
  return parsed === undefined ? undefined : Math.round(parsed);
};

const toText = (value?: string | null) => value?.trim() || undefined;

const toUnit = (value?: string | null): LengthUnit | undefined =>
  value === 'ft' || value === 'm' ? value : undefined;

/** A dimension the backend keeps in metres; skipped when the document gave no unit. */
const toMetres = (value?: number | string | null, unit?: string | null) => {
  const parsed = toNumber(value);
  const from = toUnit(unit);
  if (parsed === undefined || !from) return undefined;
  return Math.round(convertLength(parsed, from, 'm') * 100) / 100;
};

/** Length overall is canonical feet; without a unit the value is taken as printed. */
const toCanonicalFeet = (value?: number | string | null, unit?: string | null) => {
  const parsed = toNumber(value);
  if (parsed === undefined) return undefined;
  const from = toUnit(unit);
  const feet = from ? convertLength(parsed, from, 'ft') : parsed;
  return Math.round(feet * 10) / 10;
};

const pickOption = (value: string | null | undefined, options: readonly string[]) => {
  const text = value?.trim().toLowerCase();
  return text ? options.find((option) => option.toLowerCase() === text) : undefined;
};

/** Every option the printed cruising areas mention ("Bahamas & Caribbean, USA East Coast"). */
const pickCruisingAreas = (value?: string | null) => {
  const text = value?.toLowerCase();
  if (!text) return undefined;
  const matched = CRUISING_LOCATION_OPTIONS.filter((option) => text.includes(option.toLowerCase()));
  return matched.length ? matched : undefined;
};

const compact = <T extends object>(record: T): T =>
  Object.fromEntries(Object.entries(record).filter(([, v]) => v !== undefined)) as T;

export const toVesselAutofill = (extracted?: ExtractedVessel | null): VesselAutofill | null => {
  if (!extracted) return null;

  const year = toInteger(extracted.year_built);
  const vesselType = pickOption(extracted.vessel_type, VESSEL_TYPE_OPTIONS);
  const hullMaterial = pickOption(extracted.hull_material, HULL_MATERIAL_OPTIONS);

  const vessel = compact<VesselAutofill['vessel']>({
    vessel_name: toText(extracted.vessel_name),
    make: toText(extracted.make),
    model: toText(extracted.model),
    year_built: year,
    length_overall: toCanonicalFeet(extracted.length_overall, extracted.length_unit),
    registration_number: toText(extracted.registration_number),
    description: toText(extracted.description),
  });

  const answers = compact<VesselAutofill['answers']>({
    flag_state: extracted.country ? findCountryCode(extracted.country) : undefined,
    vessel_type: vesselType ? [vesselType] : undefined,
    country_built: extracted.country_built ? findCountryCode(extracted.country_built) : undefined,
    beam_m: toMetres(extracted.beam, extracted.beam_unit),
    draft_m: toMetres(extracted.draft, extracted.draft_unit),
    engines: toText(extracted.engines),
    hull_material: hullMaterial ? [hullMaterial] : undefined,
    guest_cabins: toInteger(extracted.guest_cabins),
    crew_count: toInteger(extracted.crew_count),
    home_port: toText(extracted.home_port),
    cruising_areas: pickCruisingAreas(extracted.cruising_areas),
    usage_type: pickOption(extracted.usage_type, USAGE_TYPE_OPTIONS),
    estimated_value: toNumber(extracted.estimated_value),
  });

  return Object.keys(vessel).length || Object.keys(answers).length ? { vessel, answers } : null;
};
